import type { Message, Part, Session } from "@mux/sdk/v2/client"
import { indexedDBSource } from "./browser"

const DATABASE = "mux-transcript-replica"
const VERSION = 2
const STORE = "transcripts"
const MESSAGES = "messages"
const MAX_TRANSCRIPTS = 80

export type TranscriptReplica = {
  server: string
  directory: string
  sessionID: string
  /** Cached metadata lets a deep-linked chat choose its surface before session.get returns. */
  session?: Session
  messages: Message[]
  parts: Array<{ messageID: string; parts: Part[] }>
  cursor?: string
  complete: boolean
  capturedAt: number
}

type StoredTranscriptReplica = TranscriptReplica & { key: string }
type Manifest = Omit<StoredTranscriptReplica, "messages" | "parts"> & { messageIDs: string[] }
type MessageRecord = { key: string; transcript: string; message: Message; parts: Part[] }
const messageKey = (transcript: string, id: string) => JSON.stringify([transcript, id])

const replicaKey = (server: string, directory: string, sessionID: string) =>
  JSON.stringify([server, directory, sessionID])

function parseReplicaKey(key: IDBValidKey) {
  if (typeof key !== "string") return
  try {
    const value: unknown = JSON.parse(key)
    if (Array.isArray(value) && value.length === 3 && value.every((part) => typeof part === "string")) {
      const [server, directory, sessionID] = value as [string, string, string]
      return { server, directory, sessionID }
    }
  } catch {
    // Not a replica key.
  }
}

function requestResult<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"))
  })
}

function transactionDone(transaction: IDBTransaction) {
  const done = new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted"))
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed"))
  })
  // A request can reject before its owner reaches `await done`.
  void done.catch(() => {})
  return done
}

function validReplica(value: unknown): value is StoredTranscriptReplica {
  if (!value || typeof value !== "object") return false
  const replica = value as Partial<StoredTranscriptReplica>
  return (
    typeof replica.key === "string" &&
    typeof replica.server === "string" &&
    typeof replica.directory === "string" &&
    typeof replica.sessionID === "string" &&
    (replica.session === undefined ||
      (typeof replica.session === "object" && replica.session !== null && replica.session.id === replica.sessionID)) &&
    Array.isArray(replica.messages) &&
    Array.isArray(replica.parts) &&
    typeof replica.complete === "boolean" &&
    typeof replica.capturedAt === "number"
  )
}

/** Disposable local replica. Runtime HTTP/SSE state remains authoritative. */
export function createTranscriptReplicaRepository(input?: {
  indexedDB?: IDBFactory
  maxTranscripts?: number
}) {
  const source = input?.indexedDB ?? indexedDBSource()
  const maxTranscripts = input?.maxTranscripts ?? MAX_TRANSCRIPTS
  let opening: Promise<IDBDatabase | undefined> | undefined
  const writes = new Set<Promise<void>>()
  const resets = new Map<string, Promise<void>>()
  let writing = Promise.resolve()
  let disposed = false

  const database = () => {
    if (disposed || !source) return Promise.resolve(undefined)
    if (opening) return opening
    opening = new Promise<IDBDatabase | undefined>((resolve) => {
      const request = source.open(DATABASE, VERSION)
      request.onupgradeneeded = () => {
        const database = request.result
        if (!database.objectStoreNames.contains(STORE)) {
          const store = database.createObjectStore(STORE, { keyPath: "key" })
          store.createIndex("capturedAt", "capturedAt")
        }
        if (!database.objectStoreNames.contains(MESSAGES)) {
          database.createObjectStore(MESSAGES, { keyPath: "key" }).createIndex("transcript", "transcript")
        }
      }
      request.onsuccess = () => {
        const result = request.result
        result.onversionchange = () => result.close()
        resolve(result)
      }
      request.onerror = () => resolve(undefined)
      request.onblocked = () => resolve(undefined)
    })
    return opening
  }

  const read = async (server: string, directory: string, sessionID: string) => {
    await resets.get(replicaKey(server, directory, sessionID))
    const db = await database()
    if (!db || disposed) return
    try {
      const transaction = db.transaction([STORE, MESSAGES], "readonly")
      const done = transactionDone(transaction)
      const value = await requestResult(transaction.objectStore(STORE).get(replicaKey(server, directory, sessionID)))
      let hydrated = value
      if (value && typeof value === "object" && "messageIDs" in value && Array.isArray(value.messageIDs)) {
        const manifest = value as Manifest
        const records = await Promise.all(manifest.messageIDs.map((id) =>
          requestResult<MessageRecord | undefined>(transaction.objectStore(MESSAGES).get(messageKey(manifest.key, id)))))
        if (records.every((record): record is MessageRecord => !!record)) {
          const { messageIDs: _, ...metadata } = manifest
          hydrated = { ...metadata, messages: records.map((record) => record.message),
            parts: records.map((record) => ({ messageID: record.message.id, parts: record.parts })) }
        }
      }
      await done
      if (!validReplica(hydrated)) return
      const { key: _, ...replica } = hydrated
      return replica
    } catch {
      return
    }
  }

  /** Transcripts cached for one server, read from keys without loading messages. */
  const list = async (server: string) => {
    const db = await database()
    if (!db || disposed) return []
    try {
      const keys = await requestResult<IDBValidKey[]>(db.transaction(STORE, "readonly").objectStore(STORE).getAllKeys())
      return keys.flatMap((key) => {
        const parsed = parseReplicaKey(key)
        return parsed?.server === server ? [{ directory: parsed.directory, sessionID: parsed.sessionID }] : []
      })
    } catch {
      return []
    }
  }

  /** Replaces the stored transcript with this one; the caller's copy is not kept. */
  const write = (replica: TranscriptReplica) => {
    if (disposed) return Promise.resolve()
    const ready = database()
    // Serialize cache decisions with transactions, including retries after failures.
    const request = writing.then(async () => {
      const db = await ready
      if (!db) return
      let transaction: IDBTransaction | undefined
      try {
        transaction = db.transaction([STORE, MESSAGES], "readwrite")
        const done = transactionDone(transaction)
        const store = transaction.objectStore(STORE)
        const messages = transaction.objectStore(MESSAGES)
        const key = replicaKey(replica.server, replica.directory, replica.sessionID)
        const previous = await requestResult<Manifest | StoredTranscriptReplica | undefined>(store.get(key))
        const parts = new Map(replica.parts.map((entry) => [entry.messageID, entry.parts]))
        const kept = new Set<string>()
        for (const message of replica.messages) {
          kept.add(message.id)
          messages.put({ message, parts: parts.get(message.id) ?? [], key: messageKey(key, message.id), transcript: key } satisfies MessageRecord)
        }
        if (previous && "messageIDs" in previous) {
          for (const id of previous.messageIDs) if (!kept.has(id)) messages.delete(messageKey(key, id))
        }
        const { messages: _, parts: __, ...metadata } = replica
        store.put({ ...metadata, key, messageIDs: [...kept] } satisfies Manifest)
        const count = await requestResult<number>(store.count())
        let remove = Math.max(0, count - maxTranscripts)
        if (remove > 0) {
          await new Promise<void>((resolve, reject) => {
            const cursor = store.index("capturedAt").openKeyCursor()
            cursor.onerror = () => reject(cursor.error ?? new Error("IndexedDB cursor failed"))
            cursor.onsuccess = () => {
              const current = cursor.result
              if (!current || remove === 0) {
                resolve()
                return
              }
              store.delete(current.primaryKey)
              const rows = messages.index("transcript").openKeyCursor(current.primaryKey)
              rows.onsuccess = () => {
                const row = rows.result
                if (!row) return
                messages.delete(row.primaryKey)
                row.continue()
              }
              remove -= 1
              current.continue()
            }
          })
        }
        await done
      } catch {
        // A synchronous cloning failure must not commit preceding message writes.
        try { transaction?.abort() } catch { /* Already completed or aborted. */ }
        // A local replica is an optimization. Network authority remains available.
      }
    })
    writing = request
    writes.add(request)
    void request.finally(() => writes.delete(request))
    return request
  }

  const remove = (server: string, directory: string, sessionID: string) => {
    if (disposed) return Promise.resolve()
    const ready = database()
    const request = writing.then(async () => {
      const db = await ready
      if (!db) return
      try {
      const transaction = db.transaction([STORE, MESSAGES], "readwrite")
      const done = transactionDone(transaction)
      const key = replicaKey(server, directory, sessionID)
      transaction.objectStore(STORE).delete(key)
      const rows = transaction.objectStore(MESSAGES).index("transcript").openKeyCursor(key)
      rows.onsuccess = () => {
        const row = rows.result
        if (!row) return
        transaction.objectStore(MESSAGES).delete(row.primaryKey)
        row.continue()
      }
      await done
      } catch {
        // Best effort.
      }
    })
    writing = request
    writes.add(request)
    void request.finally(() => writes.delete(request))
    return request
  }

  const reset = (server: string, directory: string, sessionID: string, messageIDs: readonly string[]) => {
    if (disposed) return Promise.resolve()
    const removed = new Set(messageIDs)
    const ready = database()
    const request = writing.then(async () => {
      const db = await ready
      if (!db) return
      let transaction: IDBTransaction | undefined
      try {
        transaction = db.transaction([STORE, MESSAGES], "readwrite")
        const done = transactionDone(transaction)
        const store = transaction.objectStore(STORE)
        const key = replicaKey(server, directory, sessionID)
        const previous = await requestResult<Manifest | StoredTranscriptReplica | undefined>(store.get(key))
        if (previous) {
          const ids = "messageIDs" in previous ? previous.messageIDs : previous.messages.map((message) => message.id)
          const remaining = ids.filter((id) => !removed.has(id))
          // Only reset pagination when the receipt actually removed cached history.
          // A delayed duplicate must not damage a newer paginated transcript.
          if (remaining.length !== ids.length) {
            const metadata = { ...previous, cursor: undefined, complete: remaining.length === 0 || previous.complete }
            if ("messageIDs" in previous) {
              store.put({ ...metadata, messageIDs: remaining })
            } else {
              store.put({ ...metadata,
                messages: previous.messages.filter((message) => !removed.has(message.id)),
                parts: previous.parts.filter((item) => !removed.has(item.messageID)),
              })
            }
          }
          for (const id of removed) transaction.objectStore(MESSAGES).delete(messageKey(key, id))
        }
        await done
      } catch (error) {
        try { transaction?.abort() } catch { /* Already completed or aborted. */ }
        throw error
      }
    })
    writing = request.catch(() => {})
    const key = replicaKey(server, directory, sessionID)
    resets.set(key, request)
    writes.add(request)
    const settled = () => {
      writes.delete(request)
      if (resets.get(key) === request) resets.delete(key)
    }
    void request.then(settled, settled)
    return request
  }

  return {
    available: !!source,
    list,
    read,
    write,
    remove,
    reset,
    dispose() {
      disposed = true
      resets.clear()
      const pending = [...writes]
      void Promise.allSettled(pending).then(() => opening?.then((db) => db?.close()))
    },
  }
}

export type TranscriptReplicaRepository = ReturnType<typeof createTranscriptReplicaRepository>
